import { Icon as IconifyIcon } from '@iconify/react';
import { styled } from '@linaria/react';
import { Radio as MantineRadio, Pill, Select, TagsInput } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useMemo, useState } from 'react';
import { useAutomationEmailRecipientSuggestions } from '../../../../api/automations-workflows-client/automations-workflows-client.hooks';
import {
  type DeliveryChannel,
  DeliveryChannelType,
  SlackDeliveryTarget,
  TeamsDeliveryTarget,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import {
  useSlackChannels,
  useSlackStatus,
} from '../../../../api/slack-integrations-client/slack-integrations-client.hooks';
import {
  useTeamsChannels,
  useTeamsStatus,
} from '../../../../api/teams-integrations-client/teams-integrations-client.hooks';
import iconTeams from '../../../../assets/icons/iconTeams.svg';
import { useFeatureFlag } from '../../../../helpers/feature-flags/feature-flags.hooks';
import { FeatureFlag } from '../../../../helpers/feature-flags/feature-flags.type';
import { Loader } from '../../../../ui-library/loader/loader';
import { Radio } from '../../../../ui-library/radio/radio';
import { Text } from '../../../../ui-library/typography/typography';
import { getFloraConnectionPath } from '../agent-settings/agent-settings.constants';
import { FloraConnectionId } from '../agent-settings/agent-settings.type';
import { inputStyles } from '../shared/input-styles';
import {
  buildSlackChannel,
  buildTeamsChannel,
  channelsToFormState,
  isValidEmail,
  normalizeRecipients,
} from './agent-delivery-channels.helpers';

/** Settings pages the "Connect Slack/Teams" links open in a new tab. */
const SLACK_CONNECTION_PATH = getFloraConnectionPath(FloraConnectionId.Slack);
const TEAMS_CONNECTION_PATH = getFloraConnectionPath(FloraConnectionId.Teams);

type AgentDeliveryChannelsProps = {
  value: DeliveryChannel[];
  onChange: (channels: DeliveryChannel[]) => void;
  onValidityChange?: (isValid: boolean) => void;
  userEmail: string;
  userId: string | null;
  fieldIdPrefix: string;
};

/**
 * Configures optional email, Slack, or Teams delivery for an automation.
 *
 * @param {AgentDeliveryChannelsProps} props - Controlled delivery-channel state and user context
 * @returns {JSX.Element} Delivery method controls and provider-specific configuration
 */
export function AgentDeliveryChannels({
  value,
  onChange,
  onValidityChange,
  userEmail,
  userId,
  fieldIdPrefix,
}: AgentDeliveryChannelsProps) {
  const initialState = useMemo(() => channelsToFormState(value, userEmail), [value, userEmail]);
  const [emailEnabled, setEmailEnabled] = useState(initialState.emailEnabled);
  const [emailRecipients, setEmailRecipients] = useState<string[]>(initialState.emailRecipients);
  const [emailRecipientSearch, setEmailRecipientSearch] = useState('');
  const [debouncedEmailRecipientSearch] = useDebouncedValue(emailRecipientSearch, 250);
  const [isEmailRecipientSearchFocused, setIsEmailRecipientSearchFocused] = useState(false);
  const [slackEnabled, setSlackEnabled] = useState(initialState.slackEnabled);
  const [slackTarget, setSlackTarget] = useState<SlackDeliveryTarget>(initialState.slackTarget);
  const [slackChannelId, setSlackChannelId] = useState<string | null>(initialState.slackChannelId);
  const [slackChannelName, setSlackChannelName] = useState<string | null>(initialState.slackChannelName);
  const [teamsEnabled, setTeamsEnabled] = useState(initialState.teamsEnabled);
  const [teamsTarget, setTeamsTarget] = useState<TeamsDeliveryTarget>(initialState.teamsTarget);
  const [teamsTeamId, setTeamsTeamId] = useState<string | null>(initialState.teamsTeamId);
  const [teamsChannelId, setTeamsChannelId] = useState<string | null>(initialState.teamsChannelId);
  const [teamsChannelName, setTeamsChannelName] = useState<string | null>(initialState.teamsChannelName);
  const [hasHydrated, setHasHydrated] = useState(false);

  const {
    suggestions: emailRecipientSuggestions,
    isLoading: emailRecipientSuggestionsLoading,
    error: emailRecipientSuggestionsError,
  } = useAutomationEmailRecipientSuggestions(debouncedEmailRecipientSearch, emailEnabled);
  const normalizedEmailRecipientSearch = emailRecipientSearch.trim();
  const normalizedDebouncedEmailRecipientSearch = debouncedEmailRecipientSearch.trim();
  const emailRecipientOptions = useMemo(
    () =>
      normalizedDebouncedEmailRecipientSearch
        ? emailRecipientSuggestions.map((suggestion) => ({
            value: suggestion.email,
            label: suggestion.name ? `${suggestion.name} ${suggestion.email}` : suggestion.email,
          }))
        : [],
    [normalizedDebouncedEmailRecipientSearch, emailRecipientSuggestions],
  );
  const emailRecipientSuggestionByEmail = useMemo(
    () => new Map(emailRecipientSuggestions.map((suggestion) => [suggestion.email, suggestion])),
    [emailRecipientSuggestions],
  );
  const showEmailRecipientSuggestions =
    isEmailRecipientSearchFocused &&
    normalizedEmailRecipientSearch.length > 0 &&
    normalizedEmailRecipientSearch === normalizedDebouncedEmailRecipientSearch &&
    emailRecipientOptions.length > 0;

  useEffect(() => {
    const next = channelsToFormState(value, userEmail);
    setEmailEnabled(next.emailEnabled);
    setEmailRecipients(next.emailRecipients);
    setSlackEnabled(next.slackEnabled);
    setSlackTarget(next.slackTarget);
    setSlackChannelId(next.slackChannelId);
    setSlackChannelName(next.slackChannelName);
    setTeamsEnabled(next.teamsEnabled);
    setTeamsTarget(next.teamsTarget);
    setTeamsTeamId(next.teamsTeamId);
    setTeamsChannelId(next.teamsChannelId);
    setTeamsChannelName(next.teamsChannelName);
    setHasHydrated(true);
    // We hydrate the picker from `value` whenever the consumer swaps in a
    // different workflow (e.g. re-opening the panel for a new row). Re-running
    // on the local form state would clobber in-progress edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, userEmail]);

  const isSlackIntegrationEnabled = useFeatureFlag(FeatureFlag.FloraSlackIntegration);
  // Pause the status fetch entirely when the integration flag is off — mirrors
  // how the integrations page gates this same query.
  const { data: slackStatus } = useSlackStatus(isSlackIntegrationEnabled);
  const slackInstalled = !!slackStatus?.installed;
  const slackUserConnected = !!slackStatus?.user_connected;
  const slackTeamId = slackStatus?.team_id ?? null;
  const slackSelfUserId = slackStatus?.slack_user_id ?? null;
  const shouldFetchChannels = slackEnabled && slackTarget === SlackDeliveryTarget.CHANNEL && slackUserConnected;
  const { data: slackChannels, query: slackChannelsQuery } = useSlackChannels(shouldFetchChannels);
  // `isLoading` (no data yet + actively fetching) flags the first load only, so
  // the spinner shows when the picker would otherwise sit empty — not on the
  // background refetch that `refetchOnMount: 'always'` triggers when cached
  // options are already rendered.
  const slackChannelsLoading = slackChannelsQuery.isLoading;
  const slackChannelOptions = useMemo(
    () =>
      (slackChannels ?? [])
        .filter((channel) => !channel.is_archived)
        .map((channel) => ({
          value: channel.id,
          label: channel.is_private ? `🔒 ${channel.name}` : `#${channel.name}`,
        })),
    [slackChannels],
  );

  const isTeamsIntegrationEnabled = useFeatureFlag(FeatureFlag.FloraTeamsIntegration);
  const { data: teamsStatus } = useTeamsStatus(isTeamsIntegrationEnabled);
  const teamsInstalled = !!teamsStatus?.installed;
  const teamsUserConnected = !!teamsStatus?.user_connected;
  const teamsTenantId = teamsStatus?.tenant_id ?? null;
  const teamsSelfAadObjectId = teamsStatus?.aad_object_id ?? null;
  const shouldFetchTeamsChannels = teamsEnabled && teamsTarget === TeamsDeliveryTarget.CHANNEL && teamsUserConnected;
  const { data: teamsChannels, query: teamsChannelsQuery } = useTeamsChannels(shouldFetchTeamsChannels);
  // Mirrors the Slack picker: spinner only while the first channel fetch is in
  // flight, never on cached-data refetches. See `slackChannelsLoading`.
  const teamsChannelsLoading = teamsChannelsQuery.isLoading;
  const teamsChannelOptions = useMemo(
    () =>
      (teamsChannels ?? [])
        .filter((channel) => !channel.is_archived)
        .map((channel) => ({
          // Each Teams channel carries its own team, so the option value is the
          // composite `team_id/channel_id` the BE returns as `id`.
          value: channel.id,
          label: `${channel.is_private ? '🔒 ' : '#'}${channel.name} · ${channel.team_name}`,
        })),
    [teamsChannels],
  );
  // The channel Select is keyed by the composite id; reconstruct it from the
  // separately-stored team/channel ids so a hydrated selection stays highlighted.
  const teamsChannelCompositeId = teamsTeamId && teamsChannelId ? `${teamsTeamId}/${teamsChannelId}` : null;

  // Slack/Teams are each offered as a delivery method only when the org has the
  // integration flag enabled AND the workspace/tenant has installed the app. A
  // workflow saved with one of them before either was turned off keeps the
  // option visible so its persisted selection still renders and can be edited.
  const slackAvailable = isSlackIntegrationEnabled && slackInstalled;
  const showSlackOption = slackAvailable || slackEnabled;
  const teamsAvailable = isTeamsIntegrationEnabled && teamsInstalled;
  const showTeamsOption = teamsAvailable || teamsEnabled;
  const deliveryMethodOptions = useMemo(
    () => [
      { value: 'email', label: 'Email' },
      ...(showSlackOption ? [{ value: 'slack', label: 'Slack' }] : []),
      ...(showTeamsOption ? [{ value: 'teams', label: 'Microsoft Teams' }] : []),
    ],
    [showSlackOption, showTeamsOption],
  );

  const invalidEmails = useMemo(() => emailRecipients.filter((email) => !isValidEmail(email)), [emailRecipients]);

  const selectedMethod: DeliveryMethod | null = emailEnabled
    ? 'email'
    : slackEnabled
      ? 'slack'
      : teamsEnabled
        ? 'teams'
        : null;

  useEffect(() => {
    if (!hasHydrated) return;
    const channels: DeliveryChannel[] = [];
    const normalized = normalizeRecipients(emailRecipients);
    const emailValid = !emailEnabled || (normalized.length > 0 && invalidEmails.length === 0);
    if (emailEnabled && emailValid) {
      channels.push({ type: DeliveryChannelType.EMAIL, recipients: normalized });
    }
    const slack = buildSlackChannel({
      slackEnabled,
      slackTarget,
      slackTeamId,
      slackSelfUserId,
      slackChannelId,
      slackChannelName,
      userId,
    });
    if (slack) channels.push(slack);

    const teams = buildTeamsChannel({
      teamsEnabled,
      teamsTarget,
      teamsTenantId,
      teamsSelfAadObjectId,
      teamsTeamId,
      teamsChannelId,
      teamsChannelName,
    });
    if (teams) channels.push(teams);

    let slackValid = !slackEnabled;
    if (slackEnabled) {
      if (!slackInstalled || !slackTeamId) slackValid = false;
      else if (slackTarget === SlackDeliveryTarget.DM) slackValid = !!slackSelfUserId;
      else if (slackTarget === SlackDeliveryTarget.CHANNEL) slackValid = slackUserConnected && !!slackChannelId;
    }

    let teamsValid = !teamsEnabled;
    if (teamsEnabled) {
      if (!teamsInstalled || !teamsTenantId) teamsValid = false;
      else if (teamsTarget === TeamsDeliveryTarget.CHAT) teamsValid = teamsUserConnected && !!teamsSelfAadObjectId;
      else if (teamsTarget === TeamsDeliveryTarget.CHANNEL)
        teamsValid = teamsUserConnected && !!teamsTeamId && !!teamsChannelId;
    }

    // Delivery is optional for automations. Leaving every method unselected is
    // valid, while a selected method must still have a complete destination.
    const isValid = emailValid && slackValid && teamsValid;

    onChange(channels);
    onValidityChange?.(isValid);
  }, [
    hasHydrated,
    emailEnabled,
    emailRecipients,
    invalidEmails,
    slackEnabled,
    slackTarget,
    slackInstalled,
    slackTeamId,
    slackSelfUserId,
    slackUserConnected,
    slackChannelId,
    slackChannelName,
    teamsEnabled,
    teamsTarget,
    teamsInstalled,
    teamsTenantId,
    teamsSelfAadObjectId,
    teamsUserConnected,
    teamsTeamId,
    teamsChannelId,
    teamsChannelName,
    userId,
    onChange,
    onValidityChange,
  ]);

  return (
    <Wrapper role="group" aria-labelledby={`${fieldIdPrefix}-delivery-label`}>
      <Select
        id={`${fieldIdPrefix}-delivery-method`}
        placeholder="Choose a delivery method"
        value={selectedMethod}
        onChange={(method) => {
          setEmailEnabled(method === 'email');
          setSlackEnabled(method === 'slack');
          setTeamsEnabled(method === 'teams');
        }}
        data={deliveryMethodOptions}
        clearable
        clearButtonProps={{ 'aria-label': 'Clear delivery method' }}
        leftSection={<DeliveryMethodIcon method={selectedMethod} />}
        renderOption={({ option }) => (
          <MethodLabel>
            <DeliveryMethodIcon method={option.value as DeliveryMethod} />
            <Text size="small" weight="medium" color="var(--ink-darkest)">
              {option.label}
            </Text>
          </MethodLabel>
        )}
        styles={inputStyles}
      />

      {emailEnabled && (
        <DeliveryMetadata>
          <EmailTagsInputWrapper>
            <EmailTagsInput
              id={`${fieldIdPrefix}-email-recipients`}
              placeholder="Add email address and press Enter"
              value={emailRecipients}
              onChange={setEmailRecipients}
              data={emailRecipientOptions}
              searchValue={emailRecipientSearch}
              onSearchChange={setEmailRecipientSearch}
              onFocus={() => setIsEmailRecipientSearchFocused(true)}
              onBlur={() => setIsEmailRecipientSearchFocused(false)}
              openOnFocus={false}
              dropdownOpened={showEmailRecipientSuggestions}
              comboboxProps={{ withinPortal: false }}
              aria-busy={emailRecipientSuggestionsLoading}
              rightSection={emailRecipientSuggestionsLoading ? <Loader size="xs" /> : undefined}
              rightSectionPointerEvents="none"
              // Mantine treats every string in splitChars as characters in a
              // regex during paste. "Enter" therefore split addresses on the
              // letters e/n/t/r; the actual Enter key is handled separately.
              splitChars={[',', ';', ' ', '\n', '\t']}
              clearable
              renderOption={({ option }) => {
                const suggestion = emailRecipientSuggestionByEmail.get(String(option.value));
                if (!suggestion) return String(option.value);
                return (
                  <EmailRecipientSuggestionOption>
                    <Text size="small" weight="medium">
                      {suggestion.name || suggestion.email}
                    </Text>
                    {suggestion.name && (
                      <EmailRecipientSuggestionEmail size="tiny">{suggestion.email}</EmailRecipientSuggestionEmail>
                    )}
                  </EmailRecipientSuggestionOption>
                );
              }}
              renderPill={({ option, onRemove }) => (
                <Pill
                  title={String(option.value)}
                  withRemoveButton
                  onRemove={onRemove}
                  styles={emailRecipientPillStyles}
                >
                  {String(option.value)}
                </Pill>
              )}
              styles={emailTagsInputStyles}
            />
          </EmailTagsInputWrapper>
          {emailRecipientSuggestionsError && normalizedEmailRecipientSearch && (
            <Text size="tiny" color="var(--red-base, #ff5247)">
              Unable to load member suggestions. You can still enter an email manually.
            </Text>
          )}
          {emailRecipients.length === 0 && (
            <Text size="tiny" color="var(--red-base, #ff5247)">
              Add at least one email recipient.
            </Text>
          )}
          {invalidEmails.length > 0 && (
            <Text size="tiny" color="var(--red-base, #ff5247)">
              Invalid {invalidEmails.length === 1 ? 'email' : 'emails'}: {invalidEmails.join(', ')}
            </Text>
          )}
        </DeliveryMetadata>
      )}

      {slackEnabled && !slackInstalled && (
        <DeliveryMetadata>
          <Text size="tiny" color="var(--ink-lighter)">
            Slack is not connected for this workspace.
          </Text>
          <ConnectLink href={SLACK_CONNECTION_PATH} target="_blank" rel="noopener noreferrer">
            <IconifyIcon icon="logos:slack-icon" width={12} height={12} />
            Connect Slack
            <IconifyIcon icon="material-symbols:open-in-new" width={12} height={12} />
          </ConnectLink>
        </DeliveryMetadata>
      )}

      {slackEnabled && slackInstalled && (
        <DeliveryMetadata>
          <MantineRadio.Group value={slackTarget} onChange={(target) => setSlackTarget(target as SlackDeliveryTarget)}>
            <TargetStack>
              <Radio value={SlackDeliveryTarget.DM} label="DM me" />
              <ChannelRow>
                <Radio value={SlackDeliveryTarget.CHANNEL} label="Channel" />
                {slackTarget === SlackDeliveryTarget.CHANNEL && slackUserConnected && (
                  <ChannelSelectWrapper>
                    <Select
                      id={`${fieldIdPrefix}-slack-channel`}
                      placeholder={slackChannelsLoading ? 'Loading channels…' : 'Pick a channel'}
                      value={slackChannelId}
                      onChange={(picked) => {
                        setSlackChannelId(picked);
                        const found = slackChannels?.find((channel) => channel.id === picked);
                        setSlackChannelName(found?.name ?? null);
                      }}
                      data={slackChannelOptions}
                      searchable
                      aria-busy={slackChannelsLoading}
                      rightSection={slackChannelsLoading ? <Loader size="xs" /> : undefined}
                      rightSectionPointerEvents="none"
                      nothingFoundMessage={slackChannelsLoading ? 'Loading channels…' : 'No channels found'}
                      styles={inputStyles}
                    />
                  </ChannelSelectWrapper>
                )}
              </ChannelRow>
            </TargetStack>
          </MantineRadio.Group>

          {slackTarget === SlackDeliveryTarget.DM && !(slackUserConnected && slackSelfUserId) && (
            <NotConnectedStack>
              <Text size="tiny" color="var(--red-base, #ff5247)">
                Your Slack account isn't connected — DMs can't be delivered.
              </Text>
              <ConnectLink href={SLACK_CONNECTION_PATH} target="_blank" rel="noopener noreferrer">
                <IconifyIcon icon="logos:slack-icon" width={12} height={12} />
                Connect your Slack account
                <IconifyIcon icon="material-symbols:open-in-new" width={12} height={12} />
              </ConnectLink>
            </NotConnectedStack>
          )}

          {slackTarget === SlackDeliveryTarget.CHANNEL && !slackUserConnected && (
            <NotConnectedStack>
              <Text size="tiny" color="var(--red-base, #ff5247)">
                Your Slack account isn't connected — channels can't be listed.
              </Text>
              <ConnectLink href={SLACK_CONNECTION_PATH} target="_blank" rel="noopener noreferrer">
                <IconifyIcon icon="logos:slack-icon" width={12} height={12} />
                Connect your Slack account
                <IconifyIcon icon="material-symbols:open-in-new" width={12} height={12} />
              </ConnectLink>
            </NotConnectedStack>
          )}
        </DeliveryMetadata>
      )}

      {teamsEnabled && !teamsInstalled && (
        <DeliveryMetadata>
          <Text size="tiny" color="var(--ink-lighter)">
            Microsoft Teams is not connected for this organization.
          </Text>
          <ConnectLink href={TEAMS_CONNECTION_PATH} target="_blank" rel="noopener noreferrer">
            <img src={iconTeams} width={12} height={12} alt="" />
            Connect Teams
            <IconifyIcon icon="material-symbols:open-in-new" width={12} height={12} />
          </ConnectLink>
        </DeliveryMetadata>
      )}

      {teamsEnabled && teamsInstalled && (
        <DeliveryMetadata>
          <MantineRadio.Group value={teamsTarget} onChange={(target) => setTeamsTarget(target as TeamsDeliveryTarget)}>
            <TargetStack>
              <Radio value={TeamsDeliveryTarget.CHAT} label="DM me" />
              <ChannelRow>
                <Radio value={TeamsDeliveryTarget.CHANNEL} label="Channel" />
                {teamsTarget === TeamsDeliveryTarget.CHANNEL && teamsUserConnected && (
                  <ChannelSelectWrapper>
                    <Select
                      id={`${fieldIdPrefix}-teams-channel`}
                      placeholder={teamsChannelsLoading ? 'Loading channels…' : 'Pick a channel'}
                      value={teamsChannelCompositeId}
                      onChange={(picked) => {
                        const found = teamsChannels?.find((channel) => channel.id === picked);
                        setTeamsTeamId(found?.team_id ?? null);
                        setTeamsChannelId(found?.channel_id ?? null);
                        setTeamsChannelName(found?.name ?? null);
                      }}
                      data={teamsChannelOptions}
                      searchable
                      aria-busy={teamsChannelsLoading}
                      rightSection={teamsChannelsLoading ? <Loader size="xs" /> : undefined}
                      rightSectionPointerEvents="none"
                      nothingFoundMessage={teamsChannelsLoading ? 'Loading channels…' : 'No channels found'}
                      styles={inputStyles}
                    />
                  </ChannelSelectWrapper>
                )}
              </ChannelRow>
            </TargetStack>
          </MantineRadio.Group>

          {teamsTarget === TeamsDeliveryTarget.CHAT && !(teamsUserConnected && teamsSelfAadObjectId) && (
            <NotConnectedStack>
              <Text size="tiny" color="var(--red-base, #ff5247)">
                Your Teams account isn't connected — DMs can't be delivered.
              </Text>
              <ConnectLink href={TEAMS_CONNECTION_PATH} target="_blank" rel="noopener noreferrer">
                <img src={iconTeams} width={12} height={12} alt="" />
                Connect your Teams account
                <IconifyIcon icon="material-symbols:open-in-new" width={12} height={12} />
              </ConnectLink>
            </NotConnectedStack>
          )}

          {teamsTarget === TeamsDeliveryTarget.CHANNEL && !teamsUserConnected && (
            <NotConnectedStack>
              <Text size="tiny" color="var(--red-base, #ff5247)">
                Your Teams account isn't connected — channels can't be listed.
              </Text>
              <ConnectLink href={TEAMS_CONNECTION_PATH} target="_blank" rel="noopener noreferrer">
                <img src={iconTeams} width={12} height={12} alt="" />
                Connect your Teams account
                <IconifyIcon icon="material-symbols:open-in-new" width={12} height={12} />
              </ConnectLink>
            </NotConnectedStack>
          )}
        </DeliveryMetadata>
      )}
    </Wrapper>
  );
}

type DeliveryMethod = 'email' | 'slack' | 'teams';

/**
 * Renders the brand icon for a delivery method option/value.
 *
 * Teams uses the bundled brand asset (matching the integrations settings page)
 * rather than an Iconify slug, which would fail silently if mistyped.
 *
 * @param {{ method: DeliveryMethod | null }} props - The delivery method key
 * @returns {JSX.Element | null} The matching icon, or null when no method is selected
 */
function DeliveryMethodIcon({ method }: { method: DeliveryMethod | null }) {
  switch (method) {
    case 'email':
      return <IconifyIcon icon="material-symbols:mail-outline" width={16} height={16} />;
    case 'slack':
      return <IconifyIcon icon="logos:slack-icon" width={14} height={14} />;
    case 'teams':
      return <img src={iconTeams} width={16} height={16} alt="" />;
    default:
      return null;
  }
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  max-width: 100%;
`;

const MethodLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const DeliveryMetadata = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 12px;
  border-left: 2px solid var(--sky-light);
  min-width: 0;
  max-width: 100%;
`;

const emailTagsInputStyles = {
  ...inputStyles,
  root: {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
  },
  wrapper: {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  input: {
    ...inputStyles.input,
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  pillsList: {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    flexWrap: 'wrap',
  },
  inputField: {
    flex: '1 1 100%',
    minWidth: 0,
    width: '100%',
  },
} as const;

const EmailTagsInput = styled(TagsInput)`
  && .mantine-TagsInput-dropdown {
    background-color: var(--base-white);
    border: var(--border-width-small) solid var(--sky-light);
    border-radius: var(--border-radius-xtiny);
    box-shadow: var(--box-shadow-small-soft);
    padding: 0.25rem;
  }

  && .mantine-TagsInput-option {
    color: var(--ink-darkest);
    border-radius: var(--border-radius-xxtiny);
    padding: 0.5rem 0.625rem;
  }

  && .mantine-TagsInput-option[data-combobox-selected] {
    background-color: var(--secondary-lightest);
    color: var(--secondary-darkest);
  }

  && .mantine-TagsInput-option:hover {
    background-color: var(--secondary-light);
    color: var(--base-white);
  }
`;

const emailRecipientPillStyles = {
  root: {
    maxWidth: '100%',
  },
  label: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
} as const;

const EmailTagsInputWrapper = styled.div`
  width: 100%;
  min-width: 0;
  max-width: 100%;
`;

const EmailRecipientSuggestionOption = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const EmailRecipientSuggestionEmail = styled(Text)`
  color: inherit;
`;

const TargetStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ChannelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const ChannelSelectWrapper = styled.div`
  flex: 1 1 220px;
  min-width: 200px;
  max-width: 360px;
`;

const ConnectLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid var(--sky-light);
  background: var(--base-white);
  color: var(--secondary-base);
  font-size: 12px;
  font-weight: 500;
  text-decoration: none;
  width: fit-content;
  transition: background 0.12s, border-color 0.12s;

  &:hover {
    background: var(--sky-lightest);
    border-color: var(--secondary-base);
  }
`;

const NotConnectedStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export type { AgentDeliveryChannelsProps };
